import { NgOptimizedImage } from '@angular/common';
import { Component, computed, inject } from '@angular/core';
import { RouterLink } from '@angular/router';
import { BreadcrumbComponent } from '../../../shared/components/breadcrumb/breadcrumb.component';
import { LucideModule } from '../../../shared/modules/lucide.module';
import {
  OUVIDORIA_NAO_PUBLICADA,
  enderecoCanal,
  prazosOuvidoria,
  responsavelOuvidoria,
  telefoneHref,
  telefoneOuvidoria,
} from './prazos-ouvidoria';
import { OuvidoriaPublicaService } from './services/ouvidoria-publica.service';

type CorCanal = 'azul' | 'verde' | 'roxo' | 'laranja' | 'ciano' | 'rosa';

interface CanalOuvidoria {
  id: string;
  titulo: string;
  icone: string;
  cor: CorCanal;
  link?: string;
}

type TipoContato = 'telefone' | 'email' | 'texto';

interface ValorContato {
  texto: string;
  tipo: TipoContato;
}

interface InformacaoOuvidoria {
  id: string;
  rotulo: string;
  icone: string;
  valores: readonly ValorContato[];
}

const CANAIS: readonly CanalOuvidoria[] = [
  {
    id: 'reclamacao',
    titulo: 'Reclamação',
    icone: 'flag',
    cor: 'rosa',
    link: '/ouvidoria/reclamacao'
  },
  {
    id: 'elogios',
    titulo: 'Elogios',
    icone: 'thumbs-up',
    cor: 'verde',
    link: '/ouvidoria/elogios'
  },
  {
    id: 'sugestoes',
    titulo: 'Sugestões',
    icone: 'lightbulb',
    cor: 'roxo',
    link: '/ouvidoria/sugestoes'
  },
  {
    id: 'solicitacao',
    titulo: 'Solicitação',
    icone: 'clipboard-list',
    cor: 'laranja',
    link: '/ouvidoria/solicitacao',
  },
  {
    id: 'denuncia',
    titulo: 'Denúncia',
    icone: 'triangle-alert',
    cor: 'rosa',
    link: '/ouvidoria/denuncia'
  },
  {
    id: 'estatisticas',
    titulo: 'Estatísticas da Ouvidoria',
    icone: 'chart-bar',
    cor: 'azul',
    link: '/estatisticas-ouvidoria',
  },
  {
    id: 'ouvidoria-presencial',
    titulo: 'Ouvidoria presencial',
    icone: 'map-pin',
    cor: 'roxo',
    link: '/ouvidoria/presencial',
  },
  {
    id: 'lgpd',
    titulo: 'LGPD',
    icone: 'shield-check',
    cor: 'ciano',
    link: '/lgpd',
  },
];

@Component({
  selector: 'app-ouvidoria',
  templateUrl: './ouvidoria.component.html',
  styleUrls: ['./ouvidoria.component.css'],
  imports: [BreadcrumbComponent, LucideModule, NgOptimizedImage, RouterLink],
})
export class OuvidoriaComponent {
  readonly canais = CANAIS;
  readonly naoPublicada = OUVIDORIA_NAO_PUBLICADA;

  /**
   * O canal do órgão, pela **mesma** leitura que o formulário de manifestação usa: o serviço fica na
   * raiz e busca uma vez por sessão, então ir e voltar de `ouvidoria/reclamacao` não relê.
   */
  private readonly publico = inject(OuvidoriaPublicaService);

  readonly canal = this.publico.canal;

  /** Enquanto a leitura não termina, o bloco não exibe nada — nem dado, nem estado vazio. */
  readonly carregando = computed(() => {
    const estado = this.publico.estadoCanal();

    return estado === 'inicial' || estado === 'carregando';
  });

  /** O nome do responsável do canal, ou "em atualização" — a linha é exibida sempre. */
  readonly responsavel = computed(() => responsavelOuvidoria(this.canal()?.responsavel));

  /** Local, e-mail, os dois telefones e horário; item sem valor sai da grade. */
  readonly informacoes = computed<readonly InformacaoOuvidoria[]>(() => {
    const canal = this.canal();

    if (!canal) return [];

    const telefones = [canal.telefonePrincipal, canal.telefoneSecundario]
      .map((telefone) => telefoneOuvidoria(telefone))
      .filter(Boolean)
      .map((texto): ValorContato => ({ texto, tipo: 'telefone' }));

    const itens: InformacaoOuvidoria[] = [
      item('local', 'Local', 'map-pin', enderecoCanal(canal.endereco), 'texto'),
      item('email', 'E-mail', 'mail', canal.email, 'email'),
      { id: 'telefone', rotulo: 'Telefone', icone: 'phone', valores: telefones },
      item('horario', 'Horário de funcionamento', 'clock', canal.horarioAtendimento, 'texto'),
    ];

    return itens.filter((info) => info.valores.length > 0);
  });

  /** Resposta (o teto do órgão) e, quando o canal permite, contestação — na unidade do canal. */
  readonly prazos = computed(() => {
    const canal = this.canal();

    return prazosOuvidoria(
      canal && {
        prazoResposta: canal.prazoPadrao,
        permiteContestacao: canal.permiteContestacao,
        prazoContestacao: canal.prazoContestacao,
        unidade: canal.unidadePrazo,
      }
    );
  });

  constructor() {
    this.publico.carregarCanal();
  }

  href(valor: ValorContato): string | null {
    switch (valor.tipo) {
      case 'telefone':
        return telefoneHref(valor.texto) || null;
      case 'email':
        return `mailto:${valor.texto}`;
      default:
        return null;
    }
  }
}

/** Um item da grade com um valor só; sem texto, fica sem valores e sai da grade. */
function item(id: string, rotulo: string, icone: string, texto: string | null | undefined, tipo: TipoContato): InformacaoOuvidoria {
  const limpo = texto?.trim();

  return { id, rotulo, icone, valores: limpo ? [{ texto: limpo, tipo }] : [] };
}
